import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { checkEventVehicleDuplicatesAsync } from '@application/features/eventVehicle/eventVehicleThunk';
const VehiclesNewVehicleDialog = ({ open, onClose, onSubmit, editingVehicle }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const isEditMode = Boolean(editingVehicle);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const supplierOptions = useMemo(() => (VDKEventsForEventDetails?.assignedCollaborators ?? []).map((collaborator) => ({
        label: `${collaborator.firstName} ${collaborator.lastName}`.trim(),
        value: collaborator.id
    })), [VDKEventsForEventDetails?.assignedCollaborators]);
    const { control, handleSubmit, watch, reset, trigger, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            vehicleName: '',
            vehicleNumber: '',
            subcontractorName: '',
            totalSeats: '',
            wheelchairSpots: '',
            carrierName: '',
            supplierId: null,
            driverNumber: ''
        }
    });
    useEffect(() => {
        if (!open)
            return;
        if (editingVehicle) {
            reset({
                vehicleName: editingVehicle.vehicleName,
                vehicleNumber: editingVehicle.vehicleNumber,
                subcontractorName: editingVehicle.subcontractorName,
                totalSeats: editingVehicle.totalSeats,
                wheelchairSpots: editingVehicle.wheelchairSpots,
                carrierName: editingVehicle.carrierName,
                supplierId: editingVehicle.supplierId || null,
                driverNumber: editingVehicle.driverNumber
            });
            return;
        }
        reset({
            vehicleName: '',
            vehicleNumber: '',
            subcontractorName: '',
            totalSeats: '',
            wheelchairSpots: '',
            carrierName: '',
            supplierId: null,
            driverNumber: ''
        });
    }, [open, editingVehicle, reset]);
    const handleCancel = () => {
        onClose();
    };
    const totalSeatsValue = watch('totalSeats');
    const wheelchairSpotsValue = watch('wheelchairSpots');
    useEffect(() => {
        if (wheelchairSpotsValue !== '' && wheelchairSpotsValue !== undefined) {
            trigger('wheelchairSpots');
        }
    }, [totalSeatsValue, trigger, wheelchairSpotsValue]);
    // Total seats can't go below the attendees already assigned to this vehicle
    const assignedCount = editingVehicle?.assignedCount ?? 0;
    const minTotalSeats = Math.max(1, assignedCount);
    // --- Duplicate check (vehicle name / vehicle number / driver number must be unique per event) ---
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const [duplicateErrors, setDuplicateErrors] = useState({});
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);
    const duplicateRequestRef = useRef(0);
    const vehicleNameValue = watch('vehicleName');
    const vehicleNumberValue = watch('vehicleNumber');
    const driverNumberValue = watch('driverNumber');
    const checkDuplicates = useCallback(async (vehicleName, vehicleNumber, driverNumber) => {
        if (!eventId || (!vehicleName?.trim() && !vehicleNumber?.trim() && !driverNumber?.trim())) {
            setDuplicateErrors({});
            return {};
        }
        const requestId = ++duplicateRequestRef.current;
        setCheckingDuplicates(true);
        try {
            const response = await dispatch(checkEventVehicleDuplicatesAsync({
                eventId,
                vehicleId: editingVehicle?.id ?? null,
                vehicleName: vehicleName?.trim() || null,
                vehicleNumber: vehicleNumber?.trim() || null,
                driverNumber: driverNumber?.trim() || null
            })).unwrap();
            const data = response?.data;
            const result = {
                vehicleName: data?.vehicleNameExists ? data.vehicleNameMessage ?? response.message : undefined,
                vehicleNumber: data?.vehicleNumberExists ? data.vehicleNumberMessage ?? response.message : undefined,
                driverNumber: data?.driverNumberExists ? data.driverNumberMessage ?? response.message : undefined
            };
            // ignore responses from an older request that finished late
            if (requestId === duplicateRequestRef.current)
                setDuplicateErrors(result);
            return result;
        }
        catch {
            // the save endpoint still validates duplicates, so a failed check doesn't block the form
            if (requestId === duplicateRequestRef.current)
                setDuplicateErrors({});
            return {};
        }
        finally {
            if (requestId === duplicateRequestRef.current)
                setCheckingDuplicates(false);
        }
    }, [dispatch, eventId, editingVehicle?.id]);
    useEffect(() => {
        if (!open) {
            duplicateRequestRef.current++;
            setDuplicateErrors({});
            setCheckingDuplicates(false);
            return;
        }
        const timer = setTimeout(() => {
            void checkDuplicates(vehicleNameValue, vehicleNumberValue, driverNumberValue);
        }, 500);
        return () => clearTimeout(timer);
    }, [open, vehicleNameValue, vehicleNumberValue, driverNumberValue, checkDuplicates]);
    const hasDuplicateErrors = Object.values(duplicateErrors).some(Boolean);
    const handleValidSubmit = async (values) => {
        // re-check right before saving in case a value changed inside the debounce window
        const result = await checkDuplicates(values.vehicleName, values.vehicleNumber, values.driverNumber);
        if (Object.values(result).some(Boolean))
            return;
        onSubmit(values);
    };
    const formContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', name: 'vehicleName', control: control, label: t('events.eventDetails.transportTab.vehicleName'), placeholder: t('events.eventDetails.transportTab.vehicleName'), rules: {
                    validate: (value) => String(value ?? '').trim().length > 0 ||
                        t('validations.required', {
                            field: t('events.eventDetails.transportTab.vehicleName')
                        })
                }, errorMessage: errors.vehicleName?.message || duplicateErrors.vehicleName }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'vehicleNumber', control: control, label: t('events.eventDetails.transportTab.vehicleNumber'), placeholder: t('events.eventDetails.transportTab.vehicleNumber'), rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.vehicleNumber')
                    })
                }, errorMessage: errors.vehicleNumber?.message || duplicateErrors.vehicleNumber }), _jsx(SingleSelect, { bg: 'grey8', name: 'supplierId', control: control, label: t('events.eventDetails.transportTab.carrierName'), placeholder: t('events.eventDetails.transportTab.carrierName'), options: supplierOptions, rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.carrierName')
                    })
                }, error: errors.supplierId?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'subcontractorName', control: control, label: t('events.eventDetails.transportTab.subcontractorName'), placeholder: t('events.eventDetails.transportTab.subcontractorName') }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'driverNumber', control: control, label: t('events.eventDetails.transportTab.driverNumber'), placeholder: t('events.eventDetails.transportTab.driverNumber'), 
                // rules={{
                //   required: t<string>('validations.required', {
                //     field: t<string>('events.eventDetails.transportTab.driverNumber')
                //   })
                // }}
                // errorMessage={errors.driverNumber?.message}
                errorMessage: duplicateErrors.driverNumber }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'totalSeats', control: control, label: t('events.eventDetails.transportTab.totalSeats'), placeholder: t('events.eventDetails.transportTab.totalSeats'), type: 'number', rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.totalSeats')
                    }),
                    min: {
                        value: minTotalSeats,
                        message: assignedCount > 1
                            ? t('events.eventDetails.transportTab.capacityBelowAssigned', { count: assignedCount })
                            : t('validations.min', {
                                field: t('events.eventDetails.transportTab.totalSeats'),
                                min: 1
                            })
                    }
                }, errorMessage: errors.totalSeats?.message }), _jsx(ControlledFilledTextField, { bg: 'grey8', name: 'wheelchairSpots', control: control, label: t('events.eventDetails.transportTab.wheelChair'), placeholder: t('events.eventDetails.transportTab.wheelChair'), type: 'number', rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.transportTab.wheelChair')
                    }),
                    min: {
                        value: 0,
                        message: t('validations.min', {
                            field: t('events.eventDetails.transportTab.wheelChair'),
                            min: 0
                        })
                    },
                    validate: (value) => {
                        const wheelchair = Number(value);
                        const total = Number(totalSeatsValue || 0);
                        if (!Number.isInteger(wheelchair)) {
                            return t('validations.wholeNumberOnly') || 'Must be a whole number.';
                        }
                        if (wheelchair > total) {
                            return (t('events.eventDetails.transportTab.wheelchairSpotsExceedTotalSeats') ||
                                'Wheelchair spots cannot exceed total seats.');
                        }
                        return true;
                    }
                }, errorMessage: errors.wheelchairSpots?.message })] }));
    const actions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: handleCancel, fullWidth: true, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', onClick: handleSubmit(handleValidSubmit), disabled: checkingDuplicates || hasDuplicateErrors, fullWidth: true, children: isEditMode ? t('actions.saveChanges') : t('actions.createNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleCancel, title: isEditMode
                ? t('events.eventDetails.transportTab.editVehicle')
                : t('events.eventDetails.transportTab.newVehicle'), anchor: 'bottom', showBottomBar: true, dialogActions: actions, children: _jsx(Box, { sx: { p: 2 }, children: formContent }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: isEditMode
            ? t('events.eventDetails.transportTab.editVehicle')
            : t('events.eventDetails.transportTab.newVehicle'), titleIcon: '/assets/icons/setting-check-icon.svg', maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, actions: actions, children: _jsx(Box, { p: 1.5, children: formContent }) }));
};
export default VehiclesNewVehicleDialog;
